/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { FrameworkLevelV3 } from '../../models/admin/framework-v3.model';
import {
  AddCompetenceLevelRequest,
  CompetenceLevel,
  CompetenceModel,
  CompetencyV3,
  CreateCompetenceRequest,
  SaveCompetencyRequest,
} from '../../models/competence/competence.model';
import { ObservableResult } from '../../store';
import { DEFAULT_LANGUAGE_CODE } from '../languages/language.service';
import { CompetenceDataService } from './competence-data.service';

export type FrameworkLevelScalePreset = 'THREE_LEVEL' | 'DREYFUS_5' | 'SFIA_7' | 'BLOOMS' | 'CUSTOM';

export const LEVEL_SCALE_PRESETS: Record<Exclude<FrameworkLevelScalePreset, 'CUSTOM'>, FrameworkLevelV3[]> = {
  THREE_LEVEL: [
    { name: 'Beginner', value: '1' },
    { name: 'Intermediate', value: '2' },
    { name: 'Advanced', value: '3' },
  ],
  DREYFUS_5: [
    { name: 'Novice', value: '1' },
    { name: 'Adv. Beginner', value: '2' },
    { name: 'Competent', value: '3' },
    { name: 'Proficient', value: '4' },
    { name: 'Expert', value: '5' },
  ],
  SFIA_7: [
    { name: 'Follow', value: '1' },
    { name: 'Assist', value: '2' },
    { name: 'Apply', value: '3' },
    { name: 'Enable', value: '4' },
    { name: 'Ensure, Advise', value: '5' },
    { name: 'Initiate, Influence', value: '6' },
    { name: 'Set Strategy', value: '7' },
  ],
  BLOOMS: [
    { name: 'Remember', value: '1' },
    { name: 'Understand', value: '2' },
    { name: 'Apply', value: '3' },
    { name: 'Analyse', value: '4' },
    { name: 'Evaluate', value: '5' },
    { name: 'Create', value: '6' },
  ],
};

@Injectable()
export class MockCompetenceDataService implements CompetenceDataService {
  competence: CompetenceModel = {
    _id: '123',
    creatorUid: 'someUid1',
    organizationUid: 'someOrgUid2',
    global: true,
    title: { languageCode: DEFAULT_LANGUAGE_CODE, value: 'Some-Competence-Title' },
    levels: [
      {
        uid: '333',
        title: [
          { languageCode: DEFAULT_LANGUAGE_CODE, value: 'Some' },
          { languageCode: DEFAULT_LANGUAGE_CODE, value: 'Competence' },
          { languageCode: DEFAULT_LANGUAGE_CODE, value: 'Title' },
        ],
        description: [{ languageCode: DEFAULT_LANGUAGE_CODE, value: 'Some description for Competence' }],
      },
    ],
  };

  createCompetence(request: CreateCompetenceRequest): ObservableResult<CompetenceModel> {
    return ObservableResult.ofSuccess(this.competence);
  }

  updateCompetence(competenceUid: string, request: CreateCompetenceRequest): ObservableResult<CompetenceModel> {
    return ObservableResult.ofSuccess(this.competence);
  }

  addCompetenceLevel(competenceUid: string, request: AddCompetenceLevelRequest): ObservableResult<CompetenceModel> {
    return ObservableResult.ofSuccess(this.competence);
  }

  updateCompetenceLevel(
    competenceUid: string,
    competenceLevelUid: string,
    request: AddCompetenceLevelRequest,
  ): ObservableResult<CompetenceLevel> {
    return ObservableResult.ofSuccess(this.competence.levels[0]);
  }

  getCompetenceById(competenceUid: string): ObservableResult<CompetenceModel> {
    return ObservableResult.ofSuccess(this.competence);
  }

  getOrganizationCompetencies(): ObservableResult<CompetenceModel[]> {
    return ObservableResult.ofSuccess([this.competence]);
  }

  connectCompetenceToTag(tagUid: string, competencyUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  private readonly globalCompetencies: CompetencyV3[] = [
    this.presetToCompetency('global-three-level', '3-Level', 'THREE_LEVEL'),
    this.presetToCompetency('global-dreyfus', '5-Level (Dreyfus)', 'DREYFUS_5'),
    this.presetToCompetency('global-sfia', 'SFIA (7-Level)', 'SFIA_7'),
    this.presetToCompetency('global-blooms', "Bloom's Taxonomy", 'BLOOMS'),
  ];

  getGlobalCompetencies(): ObservableResult<CompetencyV3[]> {
    return ObservableResult.ofSuccess(this.globalCompetencies);
  }

  saveCompetency(request: SaveCompetencyRequest): ObservableResult<CompetencyV3> {
    return ObservableResult.ofSuccess(this.requestToCompetency(`comp-${Date.now()}`, request));
  }

  updateCompetencyFull(competencyUid: string, request: SaveCompetencyRequest): ObservableResult<CompetencyV3> {
    return ObservableResult.ofSuccess(this.requestToCompetency(competencyUid, request));
  }

  private requestToCompetency(id: string, request: SaveCompetencyRequest): CompetencyV3 {
    return {
      _id: id,
      creatorUid: 'mock-user',
      organizationUid: 'mock-org',
      global: false,
      title: request.title ?? [],
      levels: request.levels.map((level, index) => ({ ...level, uid: level.uid ?? `${id}-lvl-${index + 1}` })),
    };
  }

  private presetToCompetency(id: string, title: string, preset: Exclude<FrameworkLevelScalePreset, 'CUSTOM'>): CompetencyV3 {
    return {
      _id: id,
      creatorUid: 'system',
      organizationUid: null,
      global: true,
      title: [{ languageCode: DEFAULT_LANGUAGE_CODE, value: title }],
      levels: LEVEL_SCALE_PRESETS[preset].map((level, index) => ({
        uid: `${id}-lvl-${index + 1}`,
        title: [{ languageCode: DEFAULT_LANGUAGE_CODE, value: level.name }],
        shortTitle: [{ languageCode: DEFAULT_LANGUAGE_CODE, value: level.value }],
        description: [],
        rank: index + 1,
        color: level.color ?? null,
      })),
    };
  }
}
